'use client';

import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { DESTINATION_TYPE } from '@/app/search/types.ts';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';

// The searches whose description is not the name of a place the trip lands in: an airport one
// describes itself as "Vienna (VIE)", where the code has no business in a title and the city in it
// is the airport's — this ticket's own complaint — and leisure or "anywhere" ones describe the kind
// of holiday ("Beach", "Everywhere") rather than where it happens. A hotel has been chosen by the
// time any of these are shown, so its own city is the better name for all three.
const DESTINATION_TYPES_WITHOUT_A_PLACE_NAME = [
  DESTINATION_TYPE.AIRPORT,
  DESTINATION_TYPE.ANYWHERE,
  DESTINATION_TYPE.LEISURE,
];

/**
 * The city a trip summary should name as its destination ("London – Vienna").
 *
 * MPH-957: the arrival airport's city is not the trip's city. A package to Vienna that flies into
 * Bratislava used to be titled "Bratislava", which reads as a different holiday than the one being
 * bought. The name the traveller is owed is the destination they searched for, so the title says
 * what the search box said — "Vienna", "Malta", "Costa Brava".
 *
 * The hotel's own city is the fallback: for the searches listed above, and for a destination the
 * locator could not resolve, which carries no description at all.
 *
 * The flight is deliberately not a fallback: it is where the wrong name came from, and naming it
 * while the static hotel request was still in flight only moved the bug into the first few hundred
 * milliseconds. With nothing to show, this returns `undefined` and callers keep their placeholder.
 */
export function useDestinationCityName(): string | undefined {
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { searchInfoDetailData } = usePageWrapperData();

  const searchedDestination = searchInfoDetailData?.destination;
  const searchedDestinationType = searchedDestination?.destinationType;
  const searchedDestinationName =
    searchedDestinationType && DESTINATION_TYPES_WITHOUT_A_PLACE_NAME.includes(searchedDestinationType)
      ? undefined
      : searchedDestination?.description;

  return searchedDestinationName || hotelStaticData?.city;
}
